import { Observable } from "rxjs";
import { filter, map, pairwise, startWith } from "rxjs/operators";

export type DiffType = 'added' | 'removed' | 'changed';


export type ChangeLogEvent =
  | { kind: 'add'; entry: ChangeLogEntry }
  | { kind: 'clearAll' }
  | { kind: 'clearType'; diffType: string }
  | { kind: 'clearEntry'; timestamp: string };


export interface DiffEntry {
    /** Dot / bracket path to the changed value */
    path: string;
    type: DiffType;
    previous?: unknown;
    current?: unknown;
}


export interface ChangeLogEntry {
    diffType: string;
    timestamp: string;
    changeLog: DiffEntry[];
}

export function getObservableChangeLog<T>(source$: Observable<T>, startObject: T, diffType: string = 'data'): Observable<ChangeLogEntry> {
    return withDiffs<T>(source$, startObject)
        .pipe(map(diffs => {

            return {
                diffType: diffType,
                timestamp: new Date().toISOString(),
                changeLog: diffs
            };
        }));
}

function withDiffs<T>(source$: Observable<T>, startObject: T): Observable<DiffEntry[]> {
    const differ = new ObjectDiffer();

    return source$.pipe(
        startWith(startObject),
        pairwise(),
        map(([prev, curr]) => differ.diff(prev, curr)),
        filter(diffs => diffs.length > 0)
    );
}

export class ObjectDiffer {
    /** Public entry point */
    public diff(prev: unknown, curr: unknown): DiffEntry[] {
        return this.deepDiff(prev, curr, '');
    }

    private isObject(value: unknown): value is Record<string, unknown> {
        return (
            value !== null &&
            typeof value === 'object' &&
            !Array.isArray(value)
        );
    }

    private isEqual(a: unknown, b: unknown): boolean {
        if (a === b) return true;
        if (
            typeof a === 'number' &&
            typeof b === 'number' &&
            Number.isNaN(a) &&
            Number.isNaN(b)
        ) {
            return true;
        }
        return false;
    }

    private deepDiff(prev: unknown, curr: unknown, basePath: string): DiffEntry[] {
        const diffs: DiffEntry[] = [];

        const makePath = (key: string | number): string =>
            basePath === '' ? String(key) : `${basePath}${Array.isArray(prev) ? `[${key}]` : `.${key}`}`;

        // Array vs Array
        if (Array.isArray(prev) && Array.isArray(curr)) {
            const max = Math.max(prev.length, curr.length);
            for (let i = 0; i < max; i++) {
                const p = prev[i];
                const c = curr[i];
                const path = basePath === '' ? `[${i}]` : `${basePath}[${i}]`;

                if (i >= prev.length) {
                    diffs.push({ path, type: 'added', current: c });
                } else if (i >= curr.length) {
                    diffs.push({ path, type: 'removed', previous: p });
                } else if (this.isObject(p) || Array.isArray(p) || this.isObject(c) || Array.isArray(c)) {
                    diffs.push(...this.deepDiff(p, c, path));
                } else if (!this.isEqual(p, c)) {
                    diffs.push({ path, type: 'changed', previous: p, current: c });
                }
            }
            return diffs;
        }

        // Array vs non-array — replaced
        if (Array.isArray(prev) !== Array.isArray(curr)) {
            diffs.push({
                path: basePath || '$',
                type: 'changed',
                previous: prev,
                current: curr,
            });
            return diffs;
        }

        // Object vs Object
        if (this.isObject(prev) && this.isObject(curr)) {
            const keys = new Set([...Object.keys(prev), ...Object.keys(curr)]);

            for (const key of keys) {
                const p = prev[key];
                const c = curr[key];
                const path = makePath(key);

                if (!(key in prev)) {
                    diffs.push({ path, type: 'added', current: c });
                } else if (!(key in curr)) {
                    diffs.push({ path, type: 'removed', previous: p });
                } else if (this.isObject(p) || Array.isArray(p) || this.isObject(c) || Array.isArray(c)) {
                    diffs.push(...this.deepDiff(p, c, path));
                } else if (!this.isEqual(p, c)) {
                    diffs.push({ path, type: 'changed', previous: p, current: c });
                }
            }

            return diffs;
        }

        // Primitive / type mismatch
        if (!this.isEqual(prev, curr)) {
            diffs.push({
                path: basePath || '$',
                type: 'changed',
                previous: prev,
                current: curr,
            });
        }

        return diffs;
    }
}
